import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SnippetHtml } from "@/components/SnippetHtml";
import {
  Search,
  MapPin,
  Star,
  Sparkles,
  Filter,
  X,
  Bus,
  Footprints,
  Locate,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  UtensilsCrossed,
  Flame,
  Wand2,
  RotateCcw,
  Table as TableIcon,
  LayoutGrid,
  Phone as PhoneIcon,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  SlidersHorizontal,
} from "lucide-react";
import StructuredSearchBar from "@/components/StructuredSearchBar"; // eslint-disable-line no-unused-vars
import PlatformHeader from "@/components/PlatformHeader";
import LandmarkStrip from "@/components/LandmarkStrip";
import SuiteFooter from "@/components/SuiteFooter";
import DirectoryNewsletterStrip from "@/components/DirectoryNewsletterStrip";
import { formatPhone } from "@/lib/phoneFormat";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;
const HERO_COUNT = 4; // Top 4 featured of current niche pinned at the top

const formatRating = (r) => (r ? r.toFixed(1) : "—");

// ─── Distance + Transit formatters ─────────────────────────────────────────
const formatDistanceKm = (km) => {
  if (km == null) return null;
  if (km < 1) return `${Math.round(km * 1000)}m`;
  return `${km.toFixed(km < 10 ? 1 : 0)}km`;
};
const formatDistanceM = (m) => {
  if (m == null) return null;
  if (m < 1000) return `${Math.round(m)}m`;
  return `${(m / 1000).toFixed(1)}km`;
};
const transportModeLabel = (mode) => {
  const m = (mode || "").toLowerCase();
  if (m.includes("ferry")) return "Ferry";
  if (m.includes("train") || m.includes("rail")) return "Train";
  if (m.includes("tram") || m.includes("light")) return "Tram";
  return "Bus";
};
const googleDirectionsUrl = (fromLat, fromLng, stopName, stopLat, stopLng) => {
  // Prefer lat/lng pair; fall back to the stop name + city for an open-ended search
  const dest =
    stopLat && stopLng
      ? `${stopLat},${stopLng}`
      : encodeURIComponent(`${stopName || "transport stop"} Brisbane`);
  const origin = fromLat && fromLng ? `${fromLat},${fromLng}` : "";
  return `https://www.google.com/maps/dir/?api=1&travelmode=walking&destination=${dest}${origin ? `&origin=${origin}` : ""}`;
};

// ─── 🔥 Live Demo Flip — flips Coffee Commune to Halloween for 60s then reverts ───
const FLIP_TARGETS = [
  { tpl: "tpl-halloween", icon: "🎃", label: "Halloween", rid: 6163, slug: "the-coffee-commune-bowen-hills-6163" },
  { tpl: "tpl-mothers-day", icon: "🌸", label: "Mother's Day", rid: 6163, slug: "the-coffee-commune-bowen-hills-6163" },
  { tpl: "tpl-easter", icon: "🐣", label: "Easter", rid: 6163, slug: "the-coffee-commune-bowen-hills-6163" },
  { tpl: "tpl-st-patricks", icon: "☘️", label: "St Patrick's", rid: 6163, slug: "the-coffee-commune-bowen-hills-6163" },
  { tpl: "tpl-christmas", icon: "🎄", label: "Christmas", rid: 6163, slug: "the-coffee-commune-bowen-hills-6163" },
];

function DemoFlipButton({ flip, setFlip }) {
  const [open, setOpen] = useState(false);
  const [remaining, setRemaining] = useState(0);

  // Countdown
  useEffect(() => {
    if (!flip) return;
    const tick = () => {
      const r = Math.max(0, Math.floor((new Date(flip.ends_at).getTime() - Date.now()) / 1000));
      setRemaining(r);
      if (r <= 0) {
        // Auto-revert on client
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/directory/templates/admin/live-demo-revert`, { method: "POST" })
          .then(() => setFlip(null))
          .catch(() => setFlip(null));
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [flip, setFlip]);

  const trigger = async (target) => {
    setOpen(false);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/directory/templates/admin/live-demo-flip?restaurant_id=${target.rid}&template_id=${target.tpl}&seconds=60`,
        { method: "POST" },
      );
      const j = await r.json();
      setFlip({ ...j, slug: target.slug, icon: target.icon, label: target.label });
      window.open(`/r/${target.slug}`, "_blank");
    } catch (_e) { void _e; }
  };

  const cancel = async () => {
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/directory/templates/admin/live-demo-revert`, { method: "POST" });
    setFlip(null);
  };

  if (flip && remaining > 0) {
    return (
      <div className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-fuchsia-600 to-rose-600 text-white text-[12px] font-bold shadow-lg animate-pulse" data-testid="demo-flip-active">
        <Flame className="w-3.5 h-3.5" />
        {flip.icon} {flip.label} live · {remaining}s
        <button onClick={cancel} className="ml-1 underline opacity-90 hover:opacity-100" data-testid="demo-flip-cancel">cancel</button>
      </div>
    );
  }

  return (
    <div className="ml-auto relative">
      <button
        data-testid="demo-flip-trigger"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-fuchsia-600 to-rose-600 text-white text-[12px] font-bold shadow hover:shadow-lg hover:scale-[1.02] transition"
      >
        <Wand2 className="w-3.5 h-3.5" /> Live Demo Flip
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-64 bg-white border border-stone-200 rounded-xl shadow-xl py-1.5 z-40" data-testid="demo-flip-menu">
          <div className="px-3 pt-1 pb-2 text-[10px] uppercase tracking-wider text-stone-400 font-bold border-b border-stone-100">
            Flip Coffee Commune for 60s
          </div>
          {FLIP_TARGETS.map((t) => (
            <button
              key={t.tpl}
              onClick={() => trigger(t)}
              data-testid={`demo-flip-${t.tpl}`}
              className="w-full text-left px-3 py-1.5 text-[13px] hover:bg-stone-50 flex items-center gap-2"
            >
              <span className="text-base">{t.icon}</span> {t.label}
            </button>
          ))}
          <div className="px-3 pt-1.5 pb-1 text-[10px] text-stone-400 italic border-t border-stone-100 mt-1">
            Auto-reverts after 60 seconds.
          </div>
        </div>
      )}
    </div>
  );
}

const useDebounced = (value, delay = 250) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};


// ─── Hero Row ──────────────────────────────────────────────────────────────
// 4 marquee businesses pinned at the top of the directory, per current niche.
// Always image-led to immediately communicate "this is who's premium here".
const DirectoryHeroRow = ({ items, niche }) => {
  if (!items || items.length === 0) return null;
  const label = niche === "Medical" ? "Top medical practices" :
                niche === "Liquor"  ? "Top liquor venues"     :
                niche === "Tourism" ? "Top tourism venues"    :
                "Top picks";
  return (
    <section data-testid="directory-hero-row" className="mb-6">
      <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-stone-500 mb-3">
        ✨ {label}
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.slice(0, 4).map((r) => (
          <Link
            key={r.id}
            to={`/r/${r.slug}`}
            data-testid={`hero-card-${r.id}`}
            className="group relative aspect-[4/5] rounded-2xl overflow-hidden bg-stone-200"
          >
            <img
              src={r.hero_image_url}
              alt={r.name}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&q=70"; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
            {r.tier === "platinum" && (
              <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[9px] font-bold flex items-center gap-1 shadow"
                   style={{ background: "linear-gradient(135deg, #1f1f1f 0%, #4a3b1f 50%, #b08a3e 100%)" }}>
                <Sparkles className="w-2.5 h-2.5" /> PLATINUM
              </div>
            )}
            <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
              <p className="font-bold text-[14px] leading-tight line-clamp-2">{r.name}</p>
              <p className="text-[11px] opacity-90 mt-0.5 line-clamp-1">
                {r.suburb || r.district} · {r.cuisine || r.sub_niche || ""}
              </p>
              <div className="mt-1.5 flex items-center gap-2 text-[11px]">
                {r.rating != null && (
                  <span className="flex items-center gap-0.5">
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {formatRating(r.rating)}
                  </span>
                )}
                {r.is_claimed && (
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-white/20">CLAIMED</span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

// ─── Sortable column header for table view ─────────────────────────────────
const SortHeader = ({ field, label, sort, onClick, className = "" }) => {
  const active = sort.field === field;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      data-testid={`table-sort-${field}`}
      onClick={() => onClick(field)}
      className={`px-3 py-2.5 text-left text-[11px] uppercase tracking-wider font-bold cursor-pointer select-none hover:bg-stone-100 ${className} ${active ? "text-stone-900" : "text-stone-600"}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon className={`w-3 h-3 ${active ? "opacity-100" : "opacity-40"}`} />
      </span>
    </th>
  );
};

// ─── Table View ────────────────────────────────────────────────────────────
const DirectoryTable = ({ rows, sort, onSortChange, userLoc }) => (
  <div className="border border-stone-200 rounded-xl overflow-hidden bg-white" data-testid="directory-table">
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead className="bg-stone-50 border-b border-stone-200">
          <tr>
            <SortHeader field="name"     label="Business"   sort={sort} onClick={onSortChange} />
            <th className="px-3 py-2.5 text-left text-[11px] uppercase tracking-wider font-bold text-stone-600 hidden md:table-cell">Type</th>
            {userLoc && (
              <SortHeader field="distance" label="Distance" sort={sort} onClick={onSortChange} className="hidden sm:table-cell" />
            )}
            <SortHeader field="phone"    label="Phone"      sort={sort} onClick={onSortChange} className="hidden sm:table-cell" />
            <SortHeader field="suburb"   label="Suburb"     sort={sort} onClick={onSortChange} />
            <SortHeader field="postcode" label="Pcode"      sort={sort} onClick={onSortChange} className="hidden md:table-cell" />
            <SortHeader field="tnp"      label="Tnp"        sort={sort} onClick={onSortChange} className="hidden md:table-cell" />
            <SortHeader field="rating"   label="Rating"     sort={sort} onClick={onSortChange} />
            <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-bold text-stone-600">Action</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-100">
          {rows.map((r) => {
            const isPaid = r.tier === "platinum" || r.is_claimed;
            const tnp = r.nearest_transport;
            const tnpDist = tnp ? formatDistanceM(tnp.distance_m) : null;
            const tnpModeLabel = tnp ? transportModeLabel(tnp.mode) : null;
            const tnpAlt = tnp
              ? `Distance from ${tnpModeLabel}: ${tnp.name} · ${tnpDist}${tnp.walking_time_min ? ` · ${tnp.walking_time_min} min walk` : ""}${tnp.routes ? ` · routes ${tnp.routes}` : ""}`
              : "No nearby public transport on record";
            return (
              <tr
                key={r.id}
                data-testid={`table-row-${r.id}`}
                className="hover:bg-stone-50 transition-colors group"
              >
                <td className="px-3 py-2.5 max-w-xs">
                  <Link to={`/r/${r.slug}`} className="block">
                    <div className="flex items-center gap-2">
                      {r.tier === "platinum" && <Sparkles className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                      <p className="font-semibold text-stone-900 group-hover:underline truncate min-w-0">{r.name}</p>
                    </div>
                  </Link>
                </td>
                <td className="px-3 py-2.5 hidden md:table-cell text-stone-600 text-[12px] truncate max-w-[160px]" data-testid={`table-type-${r.id}`}>
                  {r.cuisine || r.sub_niche || r.niche || <span className="text-stone-300">—</span>}
                </td>
                {userLoc && (
                  <td
                    className="px-3 py-2.5 hidden sm:table-cell tabular-nums text-stone-700"
                    data-testid={`table-distance-${r.id}`}
                    title={r.distance_km != null ? `~${r.distance_km} km from your location` : "Distance unknown"}
                  >
                    {r.distance_km != null ? formatDistanceKm(r.distance_km) : <span className="text-stone-300">—</span>}
                  </td>
                )}
                <td className="px-3 py-2.5 hidden sm:table-cell whitespace-nowrap">
                  {r.phone ? (
                    <a
                      href={`tel:${r.phone}`}
                      data-testid={`table-phone-${r.id}`}
                      className="text-emerald-700 hover:underline tabular-nums inline-flex items-center gap-1 whitespace-nowrap"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <PhoneIcon className="w-3 h-3 shrink-0" />
                      {formatPhone(r.phone)}
                    </a>
                  ) : <span className="text-stone-300">—</span>}
                </td>
                <td className="px-3 py-2.5 text-stone-700">{r.suburb || "—"}</td>
                <td className="px-3 py-2.5 hidden md:table-cell text-stone-700 tabular-nums">{r.postcode || "—"}</td>
                <td className="px-3 py-2.5 hidden md:table-cell">
                  {tnp ? (
                    <a
                      href={googleDirectionsUrl(r.lat, r.lng, tnp.name, null, null)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      title={tnpAlt}
                      aria-label={tnpAlt}
                      data-testid={`table-tnp-${r.id}`}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-[11px] font-bold tabular-nums"
                    >
                      <Bus className="w-3 h-3" />
                      {tnpDist}
                    </a>
                  ) : (
                    <span
                      title={tnpAlt}
                      className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-stone-100 text-stone-400"
                    >
                      —
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <span className="inline-flex items-center gap-1 tabular-nums">
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                    {r.rating != null ? formatRating(r.rating) : <span className="text-stone-300">—</span>}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right">
                  {isPaid ? (
                    <Link
                      to={`/r/${r.slug}`}
                      data-testid={`table-book-${r.id}`}
                      className="inline-flex items-center px-3 py-1 rounded-full bg-stone-900 text-white text-[11px] font-bold hover:bg-stone-700"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Book now
                    </Link>
                  ) : (
                    <span className="text-[11px] text-stone-300">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

// ─── Compact Grid View (4 cols, small images, max 20/page) ────────────────
/**
 * Grid view — displays the SAME field set as the table (UX consistency).
 * Pagination is upstream (pageSize slices `rows` before it reaches here),
 * so we render every row we're given.
 */
const DirectoryCompactGrid = ({ rows, userLoc }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="directory-compact-grid">
    {rows.map((r) => (
      <div
        key={r.id}
        data-testid={`compact-card-${r.id}`}
        className="hp-biz-card-wrap"
      >
        {/*
          Server-rendered listing card — same server snippet powers
          the WP plugin's [hustlesnippet name="business_card"] shortcode,
          so any card upgrade you ship (badges, review stars, hero
          crop) lands on every consumer with no rebuild.
        */}
        <SnippetHtml
          scope="global"
          name="business_card"
          params={{ entity_id: r.id }}
        />
      </div>
    ))}
  </div>
);

// ─── Pagination strip (top OR bottom) ──────────────────────────────────────
const PaginationStrip = ({ currentPage, totalPages, total, pageSize, onGoPage, onChangePageSize, position = "bottom" }) => {
  if (totalPages <= 1 && position === "bottom") return null;
  const first = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const last  = Math.min(total, currentPage * pageSize);
  return (
    <div
      data-testid={`pagination-${position}`}
      className="flex flex-wrap items-center justify-between gap-3 py-3"
    >
      <div className="flex items-center gap-2 text-[12px] text-stone-600">
        <span>
          <strong>{first.toLocaleString()}</strong>–<strong>{last.toLocaleString()}</strong> of <strong>{total.toLocaleString()}</strong>
        </span>
        <span className="text-stone-300">·</span>
        <label className="flex items-center gap-1">
          <span className="text-stone-500">Per page</span>
          <select
            data-testid={`pagination-${position}-pagesize`}
            value={pageSize}
            onChange={(e) => onChangePageSize(Number(e.target.value))}
            className="rounded-md border border-stone-200 bg-white text-[12px] py-0.5 px-1.5 focus:outline-none focus:border-stone-400"
          >
            {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>
      <div className="flex items-center gap-1">
        <button
          data-testid={`pagination-${position}-prev`}
          onClick={() => onGoPage(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className="px-2 py-1 rounded-md border border-stone-200 bg-white text-stone-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-stone-400"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        <span className="text-[12px] text-stone-700 px-2">
          Page <strong>{currentPage}</strong> / {totalPages.toLocaleString()}
        </span>
        <button
          data-testid={`pagination-${position}-next`}
          onClick={() => onGoPage(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages || totalPages === 0}
          className="px-2 py-1 rounded-md border border-stone-200 bg-white text-stone-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-stone-400"
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};


const RestaurantCard = ({ r }) => (
  <Link
    to={`/r/${r.slug}`}
    data-testid={`restaurant-card-${r.id}`}
    className="group block bg-white rounded-2xl overflow-hidden border border-stone-200 hover:border-stone-300 hover:shadow-lg transition-all duration-200"
  >
    <div className="relative aspect-[16/10] bg-stone-100 overflow-hidden">
      <img
        src={r.hero_image_url}
        alt={r.name}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        onError={(e) => {
          e.target.src =
            "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&q=70";
        }}
      />
      {r.tier === "platinum" ? (
        <div
          data-testid={`platinum-badge-${r.id}`}
          className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-white text-[11px] font-bold flex items-center gap-1 shadow-lg"
          style={{
            background: "linear-gradient(135deg, #1f1f1f 0%, #4a3b1f 50%, #b08a3e 100%)",
          }}
        >
          <Sparkles className="w-3 h-3" /> PLATINUM
        </div>
      ) : r.is_featured && (
        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-amber-500 text-white text-[11px] font-semibold flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Featured
        </div>
      )}
      {r.has_menu && (
        <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-stone-900/85 backdrop-blur text-white text-[11px] font-semibold flex items-center gap-1">
          <UtensilsCrossed className="w-3 h-3" /> Menu
        </div>
      )}
      {r.niche === "Liquor" && (
        <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-purple-700/90 backdrop-blur text-white text-[11px] font-semibold flex items-center gap-1" data-testid={`niche-badge-${r.id}`}>
          🍷 {r.sub_niche || "Liquor"}
        </div>
      )}
      {r.niche === "Medical" && (
        <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-sky-700/90 backdrop-blur text-white text-[11px] font-semibold flex items-center gap-1" data-testid={`niche-badge-${r.id}`}>
          🏥 {r.sub_niche || "Medical"}
        </div>
      )}
      {r.niche === "Tourism" && (
        <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-emerald-700/90 backdrop-blur text-white text-[11px] font-semibold flex items-center gap-1" data-testid={`niche-badge-${r.id}`}>
          🧳 {r.sub_niche || "Tourism"}
        </div>
      )}
      {r.distance_km !== null && r.distance_km !== undefined && (
        <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-stone-700 text-[11px] font-semibold flex items-center gap-1">
          <Footprints className="w-3 h-3" /> {r.distance_km} km
        </div>
      )}
    </div>
    <div className="p-4">
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-serif text-[17px] leading-tight text-stone-900 line-clamp-1">
          {r.name}
        </h3>
        <div className="shrink-0 flex items-center gap-1 text-stone-700 text-sm font-semibold">
          <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
          {formatRating(r.rating)}
        </div>
      </div>
      <div className="mt-1.5 flex items-center gap-1.5 text-stone-500 text-[13px]">
        <MapPin className="w-3 h-3 shrink-0" />
        <span className="truncate">
          {r.suburb || r.postcode || (r.niche === "Liquor" || r.niche === "Medical" ? "SE QLD" : "—")}
          {r.cuisine ? ` · ${r.cuisine}` : (r.sub_niche ? ` · ${r.sub_niche}` : "")}
        </span>
      </div>
      {r.short_description && (
        <p className="mt-2 text-[13px] text-stone-600 line-clamp-2 leading-snug">
          {r.short_description}
        </p>
      )}
      <div className="mt-3 flex items-center gap-2">
        <span className="text-[11px] text-stone-400">
          {r.review_count} review{r.review_count === 1 ? "" : "s"}
        </span>
        {r.is_claimed ? (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">
            Claimed
          </span>
        ) : (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-500 font-medium">
            Unclaimed
          </span>
        )}
      </div>
    </div>
  </Link>
);

const Chip = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium border transition-all whitespace-nowrap ${
      active
        ? "bg-stone-900 text-white border-stone-900"
        : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
    }`}
  >
    {children}
  </button>
);

export default function DirectoryPage() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [suburb, setSuburb] = useState(params.get("suburb") || "");
  const [postcode, setPostcode] = useState(params.get("postcode") || "");
  // Structured-search additions
  const [stateCode, setStateCode] = useState(params.get("state") || "");
  const [city,     setCity]       = useState(params.get("city") || "");
  const [cuisine, setCuisine] = useState(params.get("cuisine") || "");
  const [minRating, setMinRating] = useState(
    params.get("min_rating") ? Number(params.get("min_rating")) : null,
  );
  const [featured, setFeatured] = useState(params.get("featured") === "1");
  const [claimed, setClaimed] = useState(params.get("claimed") === "1");
  const [hasTransport, setHasTransport] = useState(
    params.get("has_transport") === "1",
  );
  const [hasMenu, setHasMenu] = useState(params.get("has_menu") === "1");
  const [sort, setSort] = useState(params.get("sort") || "featured");
  const [offset, setOffset] = useState(() => Number(params.get("offset")) || 0);
  const [pageSize, setPageSize] = useState(() => Number(params.get("page_size")) || DEFAULT_PAGE_SIZE);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("hd_view_mode") || "table");
  const [tableSort, setTableSort] = useState({ field: null, dir: "asc" });
  const [heroFeatured, setHeroFeatured] = useState([]);
  const [maxKm, setMaxKm] = useState(() => {
    const v = params.get("max_km");
    return v ? Number(v) : null;
  });

  // Persist viewMode to localStorage
  useEffect(() => {
    try { localStorage.setItem("hd_view_mode", viewMode); } catch {}
  }, [viewMode]);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [smartMeta, setSmartMeta] = useState(null);
  const [nicheMismatch, setNicheMismatch] = useState(null);
  const [demoFlip, setDemoFlip] = useState(null); // {ends_at, template_name}
  const [niches, setNiches] = useState([]);
  const [niche, setNiche] = useState(params.get("niche") || null); // null = restaurants; "Liquor" = bottleshops

  // Hero row — top-4 featured for currently-selected niche (independent of pagination/search)
  useEffect(() => {
    const url = new URL(`${API}/api/directory/restaurants`, window.location.origin);
    if (niche) url.searchParams.set("niche", niche);
    else url.searchParams.set("niche", "all");
    url.searchParams.set("sort", "featured");
    url.searchParams.set("limit", String(HERO_COUNT));
    fetch(url.toString())
      .then((r) => r.json())
      .then((d) => setHeroFeatured(d.results || []))
      .catch(() => setHeroFeatured([]));
  }, [niche]);
  const [district, setDistrict] = useState(params.get("district") || "");
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cuisines, setCuisines] = useState([]);
  const [topSuburbs, setTopSuburbs] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  // iter358: the whole search block (niche tabs · district tabs · structured
  // cascade · quick chips) collapses into a 1-line accordion so results
  // are visible immediately. Only the compact search input stays visible.
  const [searchOpen, setSearchOpen] = useState(false);
  const [userLoc, setUserLoc] = useState(null);
  const [stats, setStats] = useState(null);
  // iter360: admin-controlled toggle for the Top Picks hero row.
  const [topPicksEnabled, setTopPicksEnabled] = useState(true);
  const resultsRef = useRef(null);

  const qDeb = useDebounced(q, 300);

  useEffect(() => {
    fetch(`${API}/api/directory/cuisines`)
      .then((r) => r.json())
      .then(setCuisines)
      .catch(() => null);
    fetch(`${API}/api/directory/suburbs`)
      .then((r) => r.json())
      .then(setTopSuburbs)
      .catch(() => null);
    fetch(`${API}/api/directory/stats`)
      .then((r) => r.json())
      .then(setStats)
      .catch(() => null);
    // iter360: fetch the admin toggle for Top Picks.
    fetch(`${API}/api/directory/admin/settings`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (d && typeof d.top_picks_enabled === "boolean") {
          setTopPicksEnabled(d.top_picks_enabled);
        }
      })
      .catch(() => null);
  }, []);

  useEffect(() => {
    setOffset(0);
  }, [qDeb, suburb, postcode, cuisine, minRating, featured, claimed, hasTransport, hasMenu, sort, stateCode, city]);

  useEffect(() => {
    const sp = new URLSearchParams();
    if (qDeb) sp.set("q", qDeb);
    if (suburb) sp.set("suburb", suburb);
    if (postcode) sp.set("postcode", postcode);
    if (cuisine) sp.set("cuisine", cuisine);
    if (minRating) sp.set("min_rating", String(minRating));
    if (featured) sp.set("featured", "1");
    if (claimed) sp.set("claimed", "1");
    if (hasTransport) sp.set("has_transport", "1");
    if (hasMenu) sp.set("has_menu", "1");
    if (niche) sp.set("niche", niche);
    if (district) sp.set("district", district);
    if (maxKm) sp.set("max_km", String(maxKm));
    if (offset) sp.set("offset", String(offset));
    if (pageSize !== DEFAULT_PAGE_SIZE) sp.set("page_size", String(pageSize));
    if (sort && sort !== "featured") sp.set("sort", sort);
    setParams(sp, { replace: true });

    setLoading(true);
    const url = new URL(`${API}/api/directory/restaurants`, window.location.origin);
    url.searchParams.set("smart", "1");  // Always enable niche-aware parsing
    if (qDeb) url.searchParams.set("q", qDeb);
    if (suburb) url.searchParams.set("suburb", suburb);
    if (postcode) url.searchParams.set("postcode", postcode);
    if (stateCode) url.searchParams.set("state", stateCode);
    if (city) url.searchParams.set("district", city);
    if (cuisine) url.searchParams.set("cuisine", cuisine);
    if (minRating) url.searchParams.set("min_rating", String(minRating));
    if (featured) url.searchParams.set("featured", "true");
    if (claimed) url.searchParams.set("claimed", "true");
    if (hasTransport) url.searchParams.set("has_transport", "true");
    if (hasMenu) url.searchParams.set("has_menu", "true");
    url.searchParams.set("sort", sort);
    url.searchParams.set("limit", String(pageSize));
    url.searchParams.set("offset", String(offset));
    if (userLoc) {
      url.searchParams.set("user_lat", String(userLoc.lat));
      url.searchParams.set("user_lng", String(userLoc.lng));
      if (maxKm) url.searchParams.set("max_km", String(maxKm));
    }

    if (niche) url.searchParams.set("niche", niche);
    if (district) url.searchParams.set("district", district);
    fetch(url.toString())
      .then((r) => r.json())
      .then((d) => {
        setResults(d.results || []);
        setTotal(d.total || 0);
        setSmartMeta(d.smart_parsed ? {
          cuisine: d.detected_cuisine, suburb: d.detected_suburb, intent: d.detected_intent,
        } : null);
        setNicheMismatch(d.niche_mismatch || null);
      })
      .catch(() => null)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDeb, suburb, postcode, cuisine, minRating, featured, claimed, hasTransport, hasMenu, sort, offset, userLoc, niche, district, pageSize, maxKm, stateCode, city]);

  // Load niches + districts once
  useEffect(() => {
    fetch(`${API}/api/directory/niches`)
      .then((r) => r.json())
      .then((d) => setNiches(d.niches || []))
      .catch(() => {});
  }, []);

  // Districts are re-fetched whenever the selected state changes so a TAS
  // visitor sees TAS councils, a QLD visitor sees SEQ regions, etc.
  useEffect(() => {
    const url = new URL(`${API}/api/directory/districts`);
    if (stateCode) url.searchParams.set("state", stateCode);
    fetch(url.toString())
      .then((r) => r.json())
      .then((d) => setDistricts(d.districts || []))
      .catch(() => {});
    // Clear the district selection if the current one no longer belongs to
    // the newly-chosen state (avoids stale filters like "Brisbane" while
    // browsing TAS).
    if (stateCode && district) {
      setDistrict("");
      setOffset(0);
    }
  }, [stateCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const requestLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setUserLoc({ lat: p.coords.latitude, lng: p.coords.longitude });
        if (sort === "featured") setSort("distance");
      },
      () => null,
      { enableHighAccuracy: false, timeout: 5000 },
    );
  };

  const clearAll = () => {
    setQ("");
    setSuburb("");
    setPostcode("");
    setCuisine("");
    setMinRating(null);
    setFeatured(false);
    setClaimed(false);
    setHasTransport(false);
    setHasMenu(false);
    setDistrict("");
    setSort("featured");
  };

  const activeFilterCount = useMemo(
    () =>
      [suburb, postcode, cuisine, minRating, featured, claimed, hasTransport, hasMenu].filter(
        Boolean,
      ).length,
    [suburb, postcode, cuisine, minRating, featured, claimed, hasTransport, hasMenu],
  );

  const totalPages = Math.ceil(total / pageSize);
  const currentPage = Math.floor(offset / pageSize) + 1;

  const goPage = (n) => {
    setOffset((n - 1) * pageSize);
    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // ── On-screen client-side sort for table view (only affects current page) ──
  const sortedResults = useMemo(() => {
    if (!tableSort.field) return results;
    const dir = tableSort.dir === "asc" ? 1 : -1;
    const getter = (r) => {
      switch (tableSort.field) {
        case "name": return (r.name || "").toLowerCase();
        case "phone": return r.phone || "";
        case "suburb": return (r.suburb || "").toLowerCase();
        case "postcode": return r.postcode || "";
        case "tnp": return r.has_transport ? 1 : 0;
        case "rating": return r.rating || 0;
        default: return 0;
      }
    };
    return [...results].sort((a, b) => {
      const va = getter(a), vb = getter(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return  1 * dir;
      return 0;
    });
  }, [results, tableSort]);

  const toggleTableSort = (field) => {
    setTableSort((s) => s.field === field
      ? { field, dir: s.dir === "asc" ? "desc" : "asc" }
      : { field, dir: "asc" });
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Emergency now lives INSIDE the platform header as a dropdown menu
          (see EmergencyMenu snippet). userLoc is threaded through so the
          "Nearest hospitals" list uses the same geolocation as results. */}

      {/* ONE header for every module — see /app/memory/AGENT_RULES.md Rule #4 */}
      <PlatformHeader
        maxWidth="max-w-6xl"
        userLoc={userLoc}
        brand={{
          name: "Local Hustle",
          subtitle: "Directory",
          logoSrc: "/logos/local-hustle-directory-light.png",
          to: "/",
        }}
        nav={[]}
        showBookCta={false}
        showCart={false}
        extras={(
          <>
            <span className="hidden md:inline text-stone-300 dark:text-stone-600">·</span>
            <span data-testid="directory-count" className="hidden md:inline text-[13px] text-stone-500 dark:text-stone-400">
              {stats ? (() => {
                // Header shows total across whichever niche is active. Falls back
                // to the aggregated "All businesses" count so the default view
                // is not restaurant-first.
                const allEntry = niches.find((x) => x.key === null);
                const n = (niches.find((x) => x.key === niche) || {});
                const count = niche
                  ? (n.count || 0)
                  : (allEntry?.count || 0);
                const label = !niche ? "businesses"
                  : niche === "Restaurants" ? "restaurants"
                  : niche === "Liquor" ? "bottleshops"
                  : niche === "Medical" ? "medical practices"
                  : niche === "Tourism" ? "tourism listings"
                  : niche === "Accommodation" ? "places to stay"
                  : niche === "Attractions" ? "attractions"
                  : niche === "Transport" ? "transport stops"
                  : niche === "Retail" ? "shops"
                  : (n.label || "listings").toLowerCase();
                return `${count.toLocaleString()} ${label}`;
              })() : ""}
            </span>
            <DemoFlipButton flip={demoFlip} setFlip={setDemoFlip} />
            <Link
              to="/add-business"
              data-testid="directory-add-business-cta"
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-600 text-white text-[12px] font-bold hover:bg-amber-700"
            >
              <Sparkles className="w-3.5 h-3.5" /> Add Your Business
            </Link>
          </>
        )}
      />

      {/* Module-specific secondary bar: niche tabs / district tabs / search / filters.
          Sticky beneath the platform header (h-16 md:h-20 → top-16 md:top-20). */}
      <section
        data-testid="directory-secondary-bar"
        className="sticky top-16 md:top-20 z-30 bg-white/95 dark:bg-stone-900/95 backdrop-blur border-b border-stone-200 dark:border-stone-700"
      >
        <div className="max-w-6xl mx-auto px-4 py-1.5">
          {/* iter358: 1-line accordion header — collapses ALL the search
              stuff (niche tabs, district tabs, structured cascade, main
              search input, quick chips) so results are visible above the
              fold. Users tap the chevron to expand and refine. */}
          <div className="flex items-center gap-2 py-1" data-testid="directory-accordion-header">
            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              data-testid="directory-search-accordion-toggle"
              className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12px] font-semibold border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-800 hover:bg-stone-100 dark:hover:bg-stone-700 text-stone-800 dark:text-stone-100"
              title={searchOpen ? "Hide filters" : "Show filters"}
            >
              {searchOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              <Search className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Search &amp; filters</span>
              {activeFilterCount > 0 && (
                <span className="ml-1 px-1.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {/* iter360: always-visible suburb + postcode inputs so users can
                narrow the 199k catalog without hunting for the filters panel. */}
            <input
              data-testid="directory-header-suburb"
              type="search"
              value={suburb}
              onChange={(e) => setSuburb(e.target.value)}
              placeholder="Suburb"
              className="w-24 sm:w-32 h-7 px-2 rounded-md border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 text-[12px] text-stone-800 dark:text-stone-100 focus:outline-none focus:border-emerald-500"
              autoComplete="off"
            />
            <input
              data-testid="directory-header-postcode"
              type="search"
              inputMode="numeric"
              maxLength={4}
              value={postcode}
              onChange={(e) => setPostcode(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="Pcode"
              className="w-16 h-7 px-2 rounded-md border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 text-[12px] text-stone-800 dark:text-stone-100 tabular-nums focus:outline-none focus:border-emerald-500"
              autoComplete="off"
            />
            <div className="flex items-center gap-1.5 min-w-0 flex-1 overflow-x-auto scrollbar-none">
              {q && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-stone-100 dark:bg-stone-800 text-[11px] text-stone-700 dark:text-stone-200 truncate max-w-[180px]" data-testid="directory-summary-q">
                  &ldquo;{q}&rdquo;
                </span>
              )}
              {niche && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-stone-100 dark:bg-stone-800 text-[11px] text-stone-700 dark:text-stone-200 whitespace-nowrap" data-testid="directory-summary-niche">
                  {niche}
                </span>
              )}
              {district && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-stone-100 dark:bg-stone-800 text-[11px] text-stone-700 dark:text-stone-200 whitespace-nowrap" data-testid="directory-summary-district">
                  {district}
                </span>
              )}
              {suburb && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-stone-100 dark:bg-stone-800 text-[11px] text-stone-700 dark:text-stone-200 whitespace-nowrap" data-testid="directory-summary-suburb">
                  {suburb}{postcode ? ` · ${postcode}` : ""}
                </span>
              )}
              {userLoc && (
                <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 text-[11px] font-semibold whitespace-nowrap" data-testid="directory-summary-near-me">
                  <Locate className="w-2.5 h-2.5" /> Near me
                </span>
              )}
            </div>
            {(q || niche || district || suburb || activeFilterCount > 0 || userLoc) && (
              <button
                type="button"
                onClick={() => { clearAll(); setNiche(null); setDistrict(""); setUserLoc(null); setMaxKm(null); }}
                data-testid="directory-summary-clear"
                className="shrink-0 inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[11px] font-semibold text-rose-600 hover:bg-rose-50"
              >
                <X className="w-3 h-3" /> Clear
              </button>
            )}
          </div>

          {searchOpen && (
          <div className="pt-1.5 space-y-1" data-testid="directory-accordion-body">
          {/* Niche switcher */}
          {niches.length > 1 && (
            <div className="flex gap-1 overflow-x-auto pb-0.5" data-testid="niche-tabs">
              {niches.map((n) => (
                <button
                  key={n.key || "restaurants"}
                  data-testid={`niche-tab-${n.key || "restaurants"}`}
                  onClick={() => { setNiche(n.key); setOffset(0); }}
                  className={`shrink-0 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap ${
                    niche === n.key
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-200 border-stone-200 dark:border-stone-700 hover:border-stone-400"
                  }`}
                >
                  <span className="mr-1">{n.icon}</span>
                  {n.label}
                  <span className="ml-1 opacity-60">({n.count.toLocaleString()})</span>
                </button>
              ))}
            </div>
          )}

          {/* District switcher (9 SEQ districts) — with NEAR ME + range chip prefix */}
          {districts.length > 0 && (
            <div className="flex gap-1 overflow-x-auto pb-0.5" data-testid="district-tabs">
              <button
                data-testid="locate-me-chip"
                onClick={() => {
                  if (!userLoc) {
                    if (!("geolocation" in navigator)) return;
                    navigator.geolocation.getCurrentPosition(
                      (pos) => {
                        setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                        if (!maxKm) setMaxKm(2);
                      },
                      () => { /* ignore — user declined */ },
                      { enableHighAccuracy: false, timeout: 8000 }
                    );
                  } else {
                    setUserLoc(null);
                    setMaxKm(null);
                  }
                  setOffset(0);
                }}
                className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border whitespace-nowrap transition-colors ${
                  userLoc
                    ? "bg-emerald-700 text-white border-emerald-700"
                    : "bg-white text-emerald-700 border-emerald-700 hover:bg-emerald-50"
                }`}
              >
                <Locate className="w-2.5 h-2.5" /> Near me
              </button>
              {userLoc && (
                <select
                  data-testid="locate-range-select"
                  value={maxKm ?? ""}
                  onChange={(e) => { setMaxKm(e.target.value ? Number(e.target.value) : null); setOffset(0); }}
                  className="shrink-0 px-1.5 py-0.5 rounded-full text-[11px] font-semibold border border-emerald-700 bg-white text-emerald-800 cursor-pointer focus:outline-none"
                >
                  <option value="">Any distance</option>
                  <option value="0.1">≤ 100m</option>
                  <option value="0.5">≤ 500m</option>
                  <option value="1">≤ 1km</option>
                  <option value="2">≤ 2km</option>
                  <option value="5">≤ 5km</option>
                  <option value="10">≤ 10km</option>
                  <option value="30">≤ 30km</option>
                </select>
              )}
              <span className="shrink-0 w-px self-stretch bg-stone-200 mx-1" />
              <button
                data-testid="district-tab-all"
                onClick={() => { setDistrict(""); setOffset(0); }}
                className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap transition-colors ${
                  !district
                    ? "bg-emerald-700 text-white border-emerald-700"
                    : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"
                }`}
              >
                All {stateCode || "AU"}
              </button>
              {districts.map((d) => (
                <button
                  key={d.key}
                  data-testid={`district-tab-${d.key.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => { setDistrict(d.key === district ? "" : d.key); setOffset(0); }}
                  className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap transition-colors ${
                    district === d.key
                      ? "bg-emerald-700 text-white border-emerald-700"
                      : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"
                  }`}
                >
                  {d.label}
                  <span className="ml-1 opacity-60">({d.count.toLocaleString()})</span>
                </button>
              ))}
            </div>
          )}

          {/* iter358: StructuredSearchBar removed — its state/city/suburb
              inputs live in the Expanded filters panel below now. This cuts
              ~130px from the accordion body so results appear above the fold. */}

          <div className="flex gap-1.5 items-stretch">
            <div className="flex-1 relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
              <input
                data-testid="directory-search-input"
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={
                  niche === "Restaurants"
                    ? 'e.g. "best Chinese in Sherwood"…'
                    : niche === "Liquor"
                    ? 'Best bottleshop in New Farm…'
                    : niche === "Medical"
                    ? 'GP in West End…'
                    : niche === "Accountants"
                    ? 'Accountant near Oxley…'
                    : (niches.find((n) => n.key === niche)?.search_placeholder
                        || 'e.g. "TaxAssist Oxley", "cafes in Sherwood", "GP near me"…')
                }
                className="w-full h-9 pl-8 pr-3 rounded-lg bg-stone-100 dark:bg-stone-800 border border-transparent focus:border-stone-300 focus:bg-white dark:focus:bg-stone-900 focus:outline-none text-[13px] placeholder:text-stone-400"
              />
            </div>
            <button
              data-testid="directory-locate-btn"
              onClick={requestLocation}
              title="Use my location"
              className={`shrink-0 h-9 px-2.5 rounded-lg border transition-colors ${
                userLoc
                  ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                  : "bg-white border-stone-200 text-stone-600 hover:border-stone-400"
              }`}
            >
              <Locate className="w-3.5 h-3.5" />
            </button>
            <button
              data-testid="directory-toggle-filters-btn"
              onClick={() => setShowFilters((v) => !v)}
              className="shrink-0 h-9 px-2.5 rounded-lg bg-white border border-stone-200 text-stone-700 hover:border-stone-400 flex items-center gap-1 text-[12px] font-medium"
            >
              <Filter className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Filters</span>
              {activeFilterCount > 0 && (
                <span className="ml-0.5 px-1 rounded-full bg-stone-900 text-white text-[10px] font-bold">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>

          {/* Smart search parsed banner — compact */}
          {smartMeta && (
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-fuchsia-50 border border-fuchsia-200 text-fuchsia-800 text-[11px]" data-testid="smart-parsed-banner">
              <Wand2 className="w-2.5 h-2.5" />
              <span className="font-bold">Understood:</span>
              {smartMeta.cuisine && <span className="px-1 rounded bg-white">{smartMeta.cuisine}</span>}
              {smartMeta.suburb && <span className="px-1 rounded bg-white">in {smartMeta.suburb}</span>}
              {smartMeta.intent === "best" && <span className="px-1 rounded bg-white">top-rated</span>}
              {smartMeta.intent === "cheap" && <span className="px-1 rounded bg-white">value-focused</span>}
              {smartMeta.intent === "near_me" && <span className="px-1 rounded bg-white">near you</span>}
            </div>
          )}

          {/* Niche-mismatch warning: "Found in another niche — switch?" — compact */}
          {nicheMismatch && nicheMismatch.matches?.length > 0 && (
            <div
              className="flex flex-wrap items-center gap-1.5 px-2 py-1 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-[11px]"
              data-testid="niche-mismatch-banner"
            >
              <span className="font-bold">
                &ldquo;{nicheMismatch.term}&rdquo; isn&rsquo;t a {nicheMismatch.current_niche === "Restaurants" ? "restaurant" : nicheMismatch.current_niche?.toLowerCase()} —
              </span>
              {nicheMismatch.matches.slice(0, 3).map((m, i) => (
                <button
                  key={i}
                  data-testid={`niche-mismatch-switch-${m.niche}`}
                  onClick={() => {
                    const target = m.niche === "Restaurants" ? null : m.niche;
                    setNiche(target);
                    setNicheMismatch(null);
                  }}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-amber-300 hover:bg-amber-100 font-bold text-amber-900"
                >
                  Switch to {m.niche}{m.top_hit?.sub_niche ? ` · ${m.top_hit.sub_niche}` : ""}
                  <span className="opacity-70">({m.count})</span>
                </button>
              ))}
              {nicheMismatch.matches[0]?.top_hit && (
                <Link
                  to={`/r/${nicheMismatch.matches[0].top_hit.slug}`}
                  className="underline font-bold text-amber-900"
                  data-testid="niche-mismatch-open-top-hit"
                >
                  Open {nicheMismatch.matches[0].top_hit.name} →
                </Link>
              )}
              <button
                onClick={() => setNicheMismatch(null)}
                className="ml-auto text-amber-700 hover:text-amber-900"
                data-testid="niche-mismatch-dismiss"
                aria-label="Dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Quick chips row — compact, iter358 */}
          <div className="-mx-4 px-4 flex gap-1 overflow-x-auto pb-0.5 scrollbar-none">
            <Chip
              active={featured}
              onClick={() => setFeatured((v) => !v)}
              testid="chip-featured"
            >
              <Sparkles className="w-2.5 h-2.5 inline mr-0.5" />
              Featured
            </Chip>
            <Chip
              active={minRating === 4}
              onClick={() => setMinRating(minRating === 4 ? null : 4)}
              testid="chip-rating-4"
            >
              <Star className="w-2.5 h-2.5 inline mr-0.5 fill-amber-400 text-amber-400" />
              4.0+
            </Chip>
            <Chip
              active={minRating === 4.5}
              onClick={() => setMinRating(minRating === 4.5 ? null : 4.5)}
              testid="chip-rating-45"
            >
              <Star className="w-2.5 h-2.5 inline mr-0.5 fill-amber-400 text-amber-400" />
              4.5+
            </Chip>
            <Chip
              active={hasMenu}
              onClick={() => setHasMenu((v) => !v)}
              testid="chip-menu"
            >
              <UtensilsCrossed className="w-2.5 h-2.5 inline mr-0.5" />
              Menu
            </Chip>
            <Chip
              active={hasTransport}
              onClick={() => setHasTransport((v) => !v)}
              testid="chip-transport"
            >
              <Bus className="w-2.5 h-2.5 inline mr-0.5" />
              Transport
            </Chip>
            <Chip
              active={claimed}
              onClick={() => setClaimed((v) => !v)}
              testid="chip-claimed"
            >
              Claimed
            </Chip>
            {activeFilterCount > 0 && (
              <button
                data-testid="chip-clear-all"
                onClick={clearAll}
                className="shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium text-rose-600 hover:bg-rose-50 flex items-center gap-0.5"
              >
                <X className="w-2.5 h-2.5" /> Clear
              </button>
            )}
          </div>
          </div>
          )}
        </div>

        {/* Expanded filters panel */}
        {showFilters && (
          <div
            data-testid="directory-filters-panel"
            className="border-t border-stone-200 bg-stone-50"
          >
            <div className="max-w-6xl mx-auto px-4 py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {niches.length > 1 && (
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-1">
                    Category
                  </label>
                  <select
                    data-testid="filter-niche"
                    value={niche || ""}
                    onChange={(e) => { setNiche(e.target.value || null); setOffset(0); }}
                    className="w-full px-3 py-2 rounded-lg border border-stone-200 bg-white text-[14px] focus:outline-none focus:border-stone-400"
                  >
                    {niches.map((n) => (
                      <option key={n.key || "restaurants"} value={n.key || ""}>
                        {n.icon} {n.label} ({n.count.toLocaleString()})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-1">
                  Suburb
                </label>
                <select
                  data-testid="filter-suburb"
                  value={suburb}
                  onChange={(e) => setSuburb(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-stone-200 bg-white text-[14px] focus:outline-none focus:border-stone-400"
                >
                  <option value="">All suburbs</option>
                  {topSuburbs.map((s) => (
                    <option key={s.name} value={s.name}>
                      {s.name} ({s.count})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-1">
                  Postcode
                </label>
                <input
                  data-testid="filter-postcode"
                  type="text"
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value)}
                  placeholder="e.g. 4000"
                  className="w-full px-3 py-2 rounded-lg border border-stone-200 bg-white text-[14px] focus:outline-none focus:border-stone-400"
                />
              </div>
              {niche === "Restaurants" && (
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-1">
                    Cuisine
                  </label>
                  <select
                    data-testid="filter-cuisine"
                    value={cuisine}
                    onChange={(e) => setCuisine(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-stone-200 bg-white text-[14px] focus:outline-none focus:border-stone-400"
                  >
                    <option value="">Any cuisine</option>
                    {cuisines.map((c) => (
                      <option key={c.name} value={c.name}>
                        {c.name} ({c.count})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-1">
                  Sort by
                </label>
                <select
                  data-testid="filter-sort"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-stone-200 bg-white text-[14px] focus:outline-none focus:border-stone-400"
                >
                  <option value="featured">Featured first</option>
                  <option value="rating">Highest rated</option>
                  <option value="reviews">Most reviewed</option>
                  <option value="name">Name (A–Z)</option>
                  {userLoc && <option value="distance">Distance from me</option>}
                </select>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Landmarks nearby — global <LandmarkStrip /> snippet.
          Location-aware: forwards suburb/postcode/state and the user's
          geolocation coords so a Brisbane user never sees Bunbury WA
          landmarks. */}
      <LandmarkStrip
        district={district || null}
        suburb={suburb || null}
        postcode={postcode || null}
        state={stateCode || null}
        lat={userLoc?.lat ?? null}
        lng={userLoc?.lng ?? null}
        limit={20}
        onSelect={(lm) => {
          // Two-part filter: (1) narrow results to the landmark's suburb
          // so the list matches what the user just tapped; (2) also seed
          // userLoc so distance sort + nearby-landmark refresh works.
          if (lm.suburb) setSuburb(lm.suburb);
          if (lm.postcode) setPostcode(lm.postcode);
          if (lm.state) setStateCode(lm.state);
          setUserLoc({ lat: lm.lat, lng: lm.lng, source: "landmark" });
          setOffset(0);
          // Scroll results into view — the strip sits above the results.
          try {
            resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
          } catch (_e) { /* ignore */ }
        }}
      />

      {/* Results */}
      <main ref={resultsRef} className="max-w-6xl mx-auto px-4 py-5">
        {/* Hero row — top 4 of currently-selected niche (admin-toggleable) */}
        {topPicksEnabled && <DirectoryHeroRow items={heroFeatured} niche={niche} />}

        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div
            className="text-[13px] text-stone-500"
            data-testid="directory-result-count"
          >
            {loading ? (
              "Searching…"
            ) : (
              <>
                <span className="font-semibold text-stone-700">
                  {total.toLocaleString()}
                </span>{" "}
                {niche === "Liquor" ? `bottleshop${total === 1 ? "" : "s"} & bar${total === 1 ? "" : "s"}` :
                 niche === "Medical" ? `medical practice${total === 1 ? "" : "s"}` :
                 niche === "Tourism" ? `tourism listing${total === 1 ? "" : "s"}` :
                 niche === "Accommodation" ? `place${total === 1 ? "" : "s"} to stay` :
                 niche === "Attractions" ? `attraction${total === 1 ? "" : "s"}` :
                 niche === "Transport" ? `transport stop${total === 1 ? "" : "s"}` :
                 niche === "Retail" ? `shop${total === 1 ? "" : "s"}` :
                 niche === "Restaurants" ? `restaurant${total === 1 ? "" : "s"}` :
                 niche === "Accountants" ? `accountant${total === 1 ? "" : "s"}` :
                 niche
                   ? `${niche.toLowerCase()} listing${total === 1 ? "" : "s"}`
                   : `business${total === 1 ? "" : "es"}`}
                {q && ` matching "${q}"`}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {userLoc ? (
              <div className="text-[12px] text-emerald-700 flex items-center gap-1">
                <Locate className="w-3 h-3" /> Using your location
              </div>
            ) : viewMode === "table" ? (
              <button
                onClick={requestLocation}
                data-testid="enable-distance-hint"
                className="text-[12px] text-stone-600 hover:text-stone-900 flex items-center gap-1 underline decoration-dotted underline-offset-2"
                title="Show a Distance column relative to your current location"
              >
                <Locate className="w-3 h-3" /> Enable Distance column
              </button>
            ) : null}
            {/* View mode toggle */}
            <div className="inline-flex rounded-lg border border-stone-200 bg-white overflow-hidden" data-testid="view-mode-toggle">
              <button
                data-testid="view-mode-table"
                onClick={() => setViewMode("table")}
                className={`px-2.5 py-1.5 text-[12px] font-semibold flex items-center gap-1 ${viewMode === "table" ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-50"}`}
              >
                <TableIcon className="w-3.5 h-3.5" /> Table
              </button>
              <button
                data-testid="view-mode-grid"
                onClick={() => setViewMode("grid")}
                className={`px-2.5 py-1.5 text-[12px] font-semibold flex items-center gap-1 ${viewMode === "grid" ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-50"}`}
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Grid
              </button>
            </div>
          </div>
        </div>

        {/* Top pagination */}
        {total > 0 && (
          <PaginationStrip
            currentPage={currentPage}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onGoPage={goPage}
            onChangePageSize={(n) => { setPageSize(n); setOffset(0); }}
            position="top"
          />
        )}

        {loading && results.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 9 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl overflow-hidden border border-stone-200 animate-pulse"
              >
                <div className="aspect-[16/10] bg-stone-200" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-stone-200 rounded w-3/4" />
                  <div className="h-3 bg-stone-100 rounded w-1/2" />
                  <div className="h-3 bg-stone-100 rounded w-full mt-2" />
                </div>
              </div>
            ))}
          </div>
        ) : results.length === 0 ? (
          <div
            className="text-center py-20 text-stone-500 dark:text-stone-400 max-w-lg mx-auto"
            data-testid="directory-empty-state"
          >
            <Search className="w-10 h-10 mx-auto mb-3 text-stone-300 dark:text-stone-600" />
            <p className="font-medium text-stone-700 dark:text-stone-200 mb-1">
              No {(niches.find(n => n.key === niche)?.label || "results").toLowerCase()}
              {district ? ` in ${district}` : ""}
              {!district && stateCode ? ` in ${stateCode}` : ""}
              {" yet"}
            </p>
            <p className="text-[13px]">
              Try a different niche{district ? " or council" : stateCode ? " or state" : ""} — or clear all filters to see everything.
            </p>
            <div className="mt-4 flex gap-2 justify-center flex-wrap">
              {district && (
                <button
                  onClick={() => { setDistrict(""); setOffset(0); }}
                  data-testid="empty-clear-district"
                  className="text-[13px] px-3 py-1.5 rounded-full bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700"
                >
                  Show all of {stateCode || "Australia"}
                </button>
              )}
              {niche && niche !== "Restaurants" && (
                <button
                  onClick={() => { setNiche(null); setOffset(0); }}
                  data-testid="empty-switch-niche"
                  className="text-[13px] px-3 py-1.5 rounded-full bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700"
                >
                  Show all businesses
                </button>
              )}
              <button
                onClick={clearAll}
                data-testid="empty-clear-all"
                className="text-[13px] px-3 py-1.5 rounded-full bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900"
              >
                Clear all filters
              </button>
            </div>
          </div>
        ) : viewMode === "table" ? (
          <DirectoryTable
            rows={sortedResults}
            sort={tableSort}
            onSortChange={toggleTableSort}
            userLoc={userLoc}
          />
        ) : (
          <DirectoryCompactGrid rows={sortedResults} userLoc={userLoc} />
        )}

        {/* Bottom pagination */}
        {total > 0 && (
          <PaginationStrip
            currentPage={currentPage}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onGoPage={goPage}
            onChangePageSize={(n) => { setPageSize(n); setOffset(0); }}
            position="bottom"
          />
        )}
      </main>

      <footer className="max-w-6xl mx-auto px-4 py-10 text-center text-[12px] text-stone-400">
        Powered by HustlePlate · Demo data ·{" "}
        <Link to="/" className="underline">
          Back to home
        </Link>
      </footer>
      <DirectoryNewsletterStrip />
      <SuiteFooter />
    </div>
  );
}
